/**
 * Created by christiancueni on 26/09/16.
 * Copyright (c) 2016 ITerativ GmbH. All rights reserved.
 */
import * as dayjs from 'dayjs';
import find from 'lodash-es/find';
import {KlassenbuchActionTypes} from "../actions/klassenbuch";
import {Comment, Weekday} from "../models";
import {SlotIndexes, ViewEvent} from "../event/event";
import {FileAttachment} from "../attachment/attachment.model";
import {DocumentOrderFilter, DocumentOrderValues} from "../my-documents/document-order-filter";
import {AdminMessage} from "../admin-messages/admin-message";
import {CustomAction} from "../actions/CustomAction";

/*
* TODO:  rename to KlassenbuchState
*/
export interface KlassenbuchState {
  calendarDays: string[];  // array containing the 5 calendar days that are shown
  selectedDate: string;
  days: ViewEvent[][];               // 2d array containing the days and for each day the events
  selectedGroups: number[];
  showIndexes: SlotIndexes[][];   // 2d array that maps to the calendar view and keeps track of the shown events
  detailEvent: ViewEvent;
  detailEventComments: Comment[];
  loadingDetail: boolean;
  loadingComments: boolean;
  showDeleteEventModal: boolean;
  updatingEvent: boolean;
  documents: FileAttachment[];
  documentOrderFilter: DocumentOrderValues;
  documentCount: number;
  documentSearchQuery: string;
  previousDocumentUrl: string | null;
  nextDocumentUrl: string | null;
  documentPage: number;
  loadingWeeks: boolean;
  adminMessages: AdminMessage[];
}

// TODO: refactor initial empty ViewEvent: use class and a default empty state method in class

export const initialKlassenbuchState: KlassenbuchState = {
  calendarDays: [],
  selectedDate: null,
  days: [],
  showIndexes: [],
  selectedGroups: [],
  detailEvent: ViewEvent.initialObject(),
  detailEventComments: [],
  loadingDetail: false,
  loadingComments: false,
  showDeleteEventModal: false,
  updatingEvent: false,
  documents: [],
  documentOrderFilter: DocumentOrderFilter.NEWEST_FIRST.key,
  documentCount: 0,
  documentSearchQuery: '',
  previousDocumentUrl: null,
  nextDocumentUrl: null,
  documentPage: 1,
  loadingWeeks: false,
  adminMessages: []
};

const NUM_SLOTS = 9;

// helper fn for this reducer


function generateDayIndexes() {
  // Create a days*slot matrix that holds a slot object.
  let dayIndexes = Array(NUM_SLOTS);
  for (let k = 0; k < NUM_SLOTS; k++) {
    dayIndexes[k] = {
      numEvents: 0,
      eventIds: [],
      displayId: -1,
      displayIndex: -1
    }
  }
  return dayIndexes
}

// function makeIndexes(dayIndexes: SlotIndexes[], days: Object[][]) {
function makeIndexes(days: Object[][]) {

  let showIndexes = Array(days.length);

  for (let i = 0; i < days.length; i++) {

    let dayIndexes = generateDayIndexes();

    for (let j = 0; j < days[i].length; j++) {
      let event: ViewEvent = <ViewEvent>days[i][j];
      if (dayIndexes[event.start].displayId === -1) {
        dayIndexes[event.start].displayId = event.id;
        dayIndexes[event.start].displayIndex = 0;
      }

      dayIndexes[event.start].numEvents++;
      dayIndexes[event.start].eventIds.push(event.id);
    }
    showIndexes[i] = dayIndexes;
  }

  return showIndexes;
}

function changeDisplayEvent(x: number, y: number, incValue: number, showIndexes: SlotIndexes[][]) {

  let slotIndexes = showIndexes[x][y];
  let displayIndex = slotIndexes.displayIndex + incValue;

  if (displayIndex > slotIndexes.numEvents - 1) {
    displayIndex = 0;
  } else if (displayIndex < 0) {
    displayIndex = slotIndexes.numEvents - 1;
  }

  let newSlotIndexes = Object.assign({}, slotIndexes, {
    displayIndex: displayIndex,
    displayId: slotIndexes.eventIds[displayIndex]
  });

  let newDayIndexes = [
    ...showIndexes[x].slice(0, y),
    newSlotIndexes,
    ...showIndexes[x].slice(y + 1)
  ];

  let newShowIndexes = [
    ...showIndexes.slice(0, x),
    newDayIndexes,
    ...showIndexes.slice(x + 1)
  ];

  return newShowIndexes;
}

// the reducer

export function reducer(state = initialKlassenbuchState, action: CustomAction): KlassenbuchState {
  switch (action.type) {

    case KlassenbuchActionTypes.UPDATE_WEEKDAYS:
      return Object.assign({}, state, {
        calendarDays: action.payload['weekdays'],
        selectedDate: Weekday.dayjsAsString(dayjs(action.payload['selectedDate']))
      });

    case KlassenbuchActionTypes.GET_EVENTS:
      return Object.assign({}, state, {
        loadingWeeks: true
      });

    case KlassenbuchActionTypes.GET_EVENTS_SUCCESS:

      // !!! THIS NEEDS REFACTORING !!!

      let numDays = state.calendarDays.length;
      let days = Array(numDays);

      // for all days of the week
      for (let i = 0; i < numDays; i++) {

        let events = [];

        for (let j = 0; j < action.payload.length; j++) {

          let event = action.payload[j];

          //event matches day in week
          //todo: also add date object to event
          if (event.date === state.calendarDays[i]) {
            let viewEvent = ViewEvent.fromEvent(event);
            events.push(viewEvent);
          }
        }
        days[i] = events;
      }

      let showIndexs = makeIndexes(days);

      return Object.assign({}, state, {
        days: days,
        showIndexes: showIndexs,
        loadingWeeks: false
      });

    case KlassenbuchActionTypes.GET_EVENTS_FAIL:
      return Object.assign({}, state, {
        loadingWeeks: false
      });

    case KlassenbuchActionTypes.UPDATE_EVENT_SUCCESS:
    case KlassenbuchActionTypes.PUBLISH_UPDATE_EVENT_SUCCESS:

      // !!! THIS NEEDS REFACTORING !!!

      let updatedEvent = ViewEvent.fromEvent(action.payload);
      let newEvents = [];
      let newDays = [];

      // find event and update Array
      for (let i = 0; i < state.calendarDays.length; i++) {

        let dayIndexes = generateDayIndexes();
        let addedNewEventToDay = false;

        let oldEventInDay = find(state.days[i], event => event['id'] === updatedEvent.id) === undefined ? false : true;

        // add event to new day
        if (!oldEventInDay && updatedEvent.date === state.calendarDays[i]) {
          newEvents = [...state.days[i], updatedEvent];

          // chose the right array to update
          let daysArray = state.days;
          if (newDays.length > 0) {
            daysArray = newDays;
          }

          newDays = [
            ...daysArray.slice(0, i),
            newEvents,
            ...daysArray.slice(i + 1)
          ];
          addedNewEventToDay = true;
        }

        for (let j = 0; j < state.days[i].length; j++) {

          let event: ViewEvent = Object.assign({}, <ViewEvent>state.days[i][j]);

          // the event's date has changed --> remove
          if (oldEventInDay && updatedEvent.date !== state.calendarDays[i]) {
            newEvents = [
              ...state.days[i].slice(0, j),
              ...state.days[i].slice(j + 1)
            ];

            // chose the right array to update
            let daysArray = state.days;
            if (newDays.length > 0) {
              daysArray = newDays;
            }
            // use lodash
            newDays = [
              ...daysArray.slice(0, i),
              newEvents,
              ...daysArray.slice(i + 1)
            ];
            // date hasn't changed --> update
            // use lodash
          } else if (!addedNewEventToDay && updatedEvent.id === event['id']) {
            newEvents = [
              ...state.days[i].slice(0, j),
              updatedEvent,
              ...state.days[i].slice(j + 1)
            ];
            // use lodash
            newDays = [
              ...state.days.slice(0, i),
              newEvents,
              ...state.days.slice(i + 1)
            ];
          }
        }
      }

      let showIndexes = makeIndexes(newDays);

      let detailEventUpdate = updatedEvent.id === state.detailEvent.id ? updatedEvent : state.detailEvent;

      return Object.assign({}, state, {
        days: newDays,
        showIndexes: showIndexes,
        detailEvent: detailEventUpdate,
        updatingEvent: false
      });

    case KlassenbuchActionTypes.ADD_EVENT_SUCCESS:
    case KlassenbuchActionTypes.ADD_EVENT_FAIL:
    case KlassenbuchActionTypes.UPDATE_EVENT_FAIL:
      return Object.assign({}, state, {
        updatingEvent: false
      });

    case KlassenbuchActionTypes.ADD_EVENT:
    case KlassenbuchActionTypes.UPDATE_EVENT:
      return Object.assign({}, state, {
        updatingEvent: true
      });

    case KlassenbuchActionTypes.SHOW_PREV_EVENT:
      return Object.assign({}, state, {
        showIndexes: changeDisplayEvent(action.payload['x'], action.payload['y'], -1, state.showIndexes)
      });

    case KlassenbuchActionTypes.SHOW_NEXT_EVENT:
      return Object.assign({}, state, {
        showIndexes: changeDisplayEvent(action.payload['x'], action.payload['y'], 1, state.showIndexes)
      });

    case KlassenbuchActionTypes.SET_SELECT_DISPLAY_GROUPS:
      return Object.assign({}, state, {
        selectedGroups: action.payload
      });

    case KlassenbuchActionTypes.ADD_SELECT_DISPLAY_GROUP:

      // check for duplicates
      let foundIndex = state.selectedGroups.find(groupId => groupId === action.payload);
      let addGroups = [...state.selectedGroups, action.payload];

      if (foundIndex) {
        addGroups = [...state.selectedGroups];
      }

      return Object.assign({}, state, {
        selectedGroups: addGroups
      });


    case KlassenbuchActionTypes.REMOVE_SELECT_DISPLAY_GROUP:
      let filtered = state.selectedGroups.filter(id => id != action.payload);
      return Object.assign({}, state, {
        selectedGroups: filtered
      });

    case KlassenbuchActionTypes.GET_EVENT:
      return Object.assign({}, state, {
        loadingDetail: true
      });

    case KlassenbuchActionTypes.GET_EVENT_FAIL:
      return Object.assign({}, state, {
        loadingDetail: false
      });

    case KlassenbuchActionTypes.GET_EVENT_SUCCESS:

      let detailEvent = ViewEvent.fromEvent(action.payload);

      return Object.assign({}, state, {
        loadingDetail: false,
        detailEvent: detailEvent,
        detailEventComments: detailEvent.comments
      });

    case KlassenbuchActionTypes.LOAD_COMMENTS:
      return Object.assign({}, state, {
        loadingComments: true,
        detailEventComments: initialKlassenbuchState.detailEventComments
      });

    case KlassenbuchActionTypes.LOAD_COMMENTS_SUCCESS:
      return Object.assign({}, state, {
        loadingComments: false,
        detailEventComments: action.payload
      });

    case KlassenbuchActionTypes.LOAD_COMMENTS_FAIL:
      return Object.assign({}, state, {
        loadingComments: false,
        detailEventComments: initialKlassenbuchState.detailEventComments
      });

    case KlassenbuchActionTypes.RESET_DETAIL_EVENT:
      return Object.assign({}, state, {
        detailEvent: initialKlassenbuchState.detailEvent
      });

    case KlassenbuchActionTypes.DELETE_EVENT_USER_ACTION:
      return Object.assign({}, state, {
        showDeleteEventModal: true
      });

    case KlassenbuchActionTypes.DELETE_EVENT_CONFIRMATION:
    case KlassenbuchActionTypes.DELETE_EVENT_CANCEL:
      return Object.assign({}, state, {
        showDeleteEventModal: false
      });

    case KlassenbuchActionTypes.UPDATE_DOCUMENT_PAGE:
      return Object.assign({}, state, {
        documentPage: action.payload,
      })

    case KlassenbuchActionTypes.UPDATE_DOCUMENT_QUERY:
      return Object.assign({}, state, {
        documentSearchQuery: action.payload,
      })

    case KlassenbuchActionTypes.GET_MY_DOCUMENTS_SUCCESS:
      return Object.assign({}, state, {
        documents: action.payload.results.map(document => FileAttachment.fromPayload(document)),
        documentCount: action.payload.count,
        nextDocumentUrl: action.payload.next,
        previousDocumentUrl: action.payload.previous
      });

    case KlassenbuchActionTypes.GET_MY_DOCUMENTS_FAIL:
      return Object.assign({}, state, {
        documents: []
      });

    case KlassenbuchActionTypes.UPDATE_DOCUMENT_FILTER:
      return Object.assign({}, state, {
        documentOrderFilter: action.payload.key
      });

    case KlassenbuchActionTypes.PRERENDER_EVENT:
    case KlassenbuchActionTypes.GET_EVENT_FROM_STORAGE_SUCCESS:
      return Object.assign({}, state, {
        detailEvent: action.payload,
        detailEventComments: action.payload.comments
      });

    case KlassenbuchActionTypes.DELETE_DOCUMENTS_SUCCESS:
      return Object.assign({}, state, {
        documents: state.documents.filter(document => !action.payload.includes(document.id)),
        documentCount: state.documentCount - action.payload.length
      })

    case KlassenbuchActionTypes.GET_EVENTS_FROM_STORAGE_SUCCESS:
      const daysFromStorage = action.payload;
      let showIndexsForDays = makeIndexes(daysFromStorage);

      return Object.assign({}, state, {
        days: daysFromStorage,
        showIndexes: showIndexsForDays,
        loadingWeeks: false
      });

    // case KlassenbuchActionTypes.LOAD_ADMIN_MESSAGES:

    case KlassenbuchActionTypes.LOAD_ADMIN_MESSAGES_FAILURE:
      return Object.assign({}, state, {
        adminMessages: []
      });

    case KlassenbuchActionTypes.LOAD_ADMIN_MESSAGES_SUCCESS:
      return Object.assign({}, state, {
        adminMessages: action.payload
      });

    case KlassenbuchActionTypes.RESET_KLASSENBUCH:
      return Object.assign({}, initialKlassenbuchState);

    //case KlassenbuchActionTypes.MARK_ADMIN_MESSAGE_READ:
    //case KlassenbuchActionTypes.MARK_ADMIN_MESSAGE_READ_SUCCESS:
    //case KlassenbuchActionTypes.MARK_ADMIN_MESSAGE_READ_FAILURE:

    default: {
      return state;
    }
  }
}

export const getCalendarDays = (state: KlassenbuchState) => state.calendarDays;

export function getDays(state: KlassenbuchState) {
  return state.days;
}

export function getSelectedGroupIds(state: KlassenbuchState) {
  return state.selectedGroups;
}

export function getshowIndexes(state: KlassenbuchState) {
  return state.showIndexes;
}

export function getSelectedDate(state: KlassenbuchState) {
  return state.selectedDate;
}

export function getDetailEvent(state: KlassenbuchState) {
  return state.detailEvent;
}

export function isDetailLoading(state: KlassenbuchState) {
  return state.loadingDetail;
}

export function isLoadingComments(state: KlassenbuchState) {
  return state.loadingComments;
}

export function getComments(state: KlassenbuchState) {
  return state.detailEventComments;
}

export const getShowDeleteEventModal = (state: KlassenbuchState) => state.showDeleteEventModal;


export function getUpdatingEvent(state: KlassenbuchState) {
  return state.updatingEvent;
}

export const getDocuments = (state: KlassenbuchState) => state.documents;

export function getDocumentOrderFilter(state: KlassenbuchState) {
  return state.documentOrderFilter;
}

export function getDocumentSearchQuery(state: KlassenbuchState) {
  return state.documentSearchQuery;
}

export function getDocumentCount(state: KlassenbuchState) {
  return state.documentCount;
}

export function getNextDocumentUrl(state: KlassenbuchState) {
  return state.nextDocumentUrl;
}

export function getPreviousDocumentUrl(state: KlassenbuchState) {
  return state.previousDocumentUrl;
}

export function getDocumentPage(state: KlassenbuchState) {
  return state.documentPage;
}

export function getLoadingWeeks(state: KlassenbuchState) {
  return state.loadingWeeks;
}

export function getAdminMessages(state: KlassenbuchState) {
  return state.adminMessages;
}
